import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography, Grid, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PublicIcon from '@mui/icons-material/Public';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import TranslateIcon from '@mui/icons-material/Translate';
import ShowChartIcon from '@mui/icons-material/ShowChart';

import useAnalyticsStore from '../../stores/analyticsStore';
import AnalyticsFilters from './components/AnalyticsHeader';
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import { MetricsCard } from '../../components/features/analytics/MetricsCard';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`regional-tabpanel-${index}`}
      aria-labelledby={`regional-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const RegionalAnalytics = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    brand: '',
    client: '',
    campaign: '',
    platform: '',
  });

  const {
    regionalAnalytics,
    fetchRegionalAnalytics,
    isLoading,
    error
  } = useAnalyticsStore();

  useEffect(() => {
    fetchRegionalAnalytics(filters);
  }, [fetchRegionalAnalytics, filters]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Color configurations
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];

  // Data transformations and mock data
  const getTopRegions = () => {
    if (!regionalAnalytics || !regionalAnalytics.regions) return [];
    return regionalAnalytics.regions.slice(0, 5);
  };

  const getRegionalGrowth = () => {
    if (!regionalAnalytics || !regionalAnalytics.growth) return [];
    return regionalAnalytics.growth;
  };

  const getLanguageData = () => {
    if (!regionalAnalytics || !regionalAnalytics.languages) return [];
    return regionalAnalytics.languages;
  };

  const getCityData = () => {
    if (!regionalAnalytics || !regionalAnalytics.cities) return [];
    return regionalAnalytics.cities.slice(0, 10);
  };

  const getEngagementByRegion = () => {
    if (!regionalAnalytics || !regionalAnalytics.engagementByRegion) return [];
    return regionalAnalytics.engagementByRegion;
  };

  const getContentPerformanceByRegion = () => {
    if (!regionalAnalytics || !regionalAnalytics.contentPerformance) return [];
    return regionalAnalytics.contentPerformance;
  };

  // Mock data for development
  const mockTopRegions = [
    { name: 'Middle East', value: 35 },
    { name: 'North Africa', value: 25 },
    { name: 'Gulf', value: 20 },
    { name: 'Europe', value: 12 },
    { name: 'North America', value: 8 },
  ];

  const mockRegionalGrowth = [
    { name: 'Jan', MiddleEast: 4000, NorthAfrica: 2400, Gulf: 2400 },
    { name: 'Feb', MiddleEast: 3000, NorthAfrica: 1398, Gulf: 2210 },
    { name: 'Mar', MiddleEast: 2000, NorthAfrica: 9800, Gulf: 2290 },
    { name: 'Apr', MiddleEast: 2780, NorthAfrica: 3908, Gulf: 2000 },
    { name: 'May', MiddleEast: 1890, NorthAfrica: 4800, Gulf: 2181 },
    { name: 'Jun', MiddleEast: 2390, NorthAfrica: 3800, Gulf: 2500 },
  ];

  const mockLanguageData = [
    { name: 'Arabic', value: 55 },
    { name: 'English', value: 30 },
    { name: 'French', value: 10 },
    { name: 'Other', value: 5 },
  ];

  const mockCityData = [
    { name: 'Cairo', value: 2500 },
    { name: 'Riyadh', value: 2200 },
    { name: 'Dubai', value: 2000 },
    { name: 'Jeddah', value: 1800 },
    { name: 'Kuwait City', value: 1500 },
    { name: 'Doha', value: 1300 },
    { name: 'Abu Dhabi', value: 1200 },
    { name: 'Amman', value: 1100 },
    { name: 'Casablanca', value: 900 },
    { name: 'Tunis', value: 700 },
  ];

  const mockEngagementByRegion = [
    { region: 'Middle East', engagement: 85, reach: 70 },
    { region: 'North Africa', engagement: 60, reach: 65 },
    { region: 'Gulf', engagement: 90, reach: 80 },
    { region: 'Europe', engagement: 40, reach: 50 },
    { region: 'North America', engagement: 30, reach: 45 },
  ];

  const mockContentPerformanceByRegion = [
    { name: 'Video', MiddleEast: 80, NorthAfrica: 60, Gulf: 75 },
    { name: 'Images', MiddleEast: 65, NorthAfrica: 70, Gulf: 62 },
    { name: 'Stories', MiddleEast: 55, NorthAfrica: 50, Gulf: 70 },
    { name: 'Text', MiddleEast: 40, NorthAfrica: 30, Gulf: 45 },
  ];

  // Use real data if available, otherwise use mock data
  const topRegions = regionalAnalytics ? getTopRegions() : mockTopRegions;
  const regionalGrowth = regionalAnalytics ? getRegionalGrowth() : mockRegionalGrowth;
  const languageData = regionalAnalytics ? getLanguageData() : mockLanguageData;
  const cityData = regionalAnalytics ? getCityData() : mockCityData;
  const engagementByRegion = regionalAnalytics ? getEngagementByRegion() : mockEngagementByRegion;
  const contentPerformanceByRegion = regionalAnalytics ? getContentPerformanceByRegion() : mockContentPerformanceByRegion;

  return (
    <Box sx={{ width: '100%' }}>
      <AnalyticsFilters 
        title="Regional Analytics"
        onFilterChange={handleFilterChange}
        showClientFilter
        showBrandFilter
        showDateRange
        exportData={() => {
          return regionalAnalytics ? regionalAnalytics : {
            topRegions,
            regionalGrowth,
            languageData,
            cityData,
            engagementByRegion,
            contentPerformanceByRegion
          };
        }}
        exportFilename="regional-analytics-report"
        isLoading={isLoading}
      />

      {error && (
        <Typography color="error" sx={{ mt: 2, mb: 2 }}>
          Error loading regional analytics: {error}
        </Typography>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="regional analytics tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<PublicIcon />} label="Overview" />
          <Tab icon={<LocationCityIcon />} label="Cities & Regions" />
          <Tab icon={<TranslateIcon />} label="Languages" />
          <Tab icon={<ShowChartIcon />} label="Performance" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <KPICard 
              title="Total Regions" 
              value={regionalAnalytics?.totalRegions || 15}
              trend={regionalAnalytics?.regionTrend?.value || 5.2}
              trendLabel={regionalAnalytics?.regionTrend?.label || "vs. last period"}
              isPositiveTrend={regionalAnalytics?.regionTrend?.isPositive || true}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <KPICard 
              title="Top Region"
              value={regionalAnalytics?.topRegionName || "Middle East"}
              subtitle={`${regionalAnalytics?.topRegionPercent || "35%"} of audience`}
              trend={regionalAnalytics?.topRegionTrend?.value || 2.1}
              trendLabel={regionalAnalytics?.topRegionTrend?.label || "growth"}
              isPositiveTrend={regionalAnalytics?.topRegionTrend?.isPositive || true}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <KPICard 
              title="Primary Language" 
              value={regionalAnalytics?.primaryLanguage || "Arabic"}
              subtitle={`${regionalAnalytics?.primaryLanguagePercent || "55%"} of content`}
              trend={regionalAnalytics?.languageTrend?.value || 0}
              trendLabel={regionalAnalytics?.languageTrend?.label || "no change"}
              isPositiveTrend={regionalAnalytics?.languageTrend?.isPositive || true}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <KPICard 
              title="Fastest Growing Region" 
              value={regionalAnalytics?.fastestGrowingRegion || "Gulf"}
              trend={regionalAnalytics?.growthRate?.value || 8.7}
              trendLabel={regionalAnalytics?.growthRate?.label || "growth rate"}
              isPositiveTrend={regionalAnalytics?.growthRate?.isPositive || true}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Audience Distribution by Region">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={topRegions}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {topRegions.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        
          <Grid item xs={12} md={6}>
            <ChartCard title="Audience Growth by Region">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={regionalGrowth}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="MiddleEast" stroke={COLORS[0]} name="Middle East" />
                  <Line type="monotone" dataKey="NorthAfrica" stroke={COLORS[1]} name="North Africa" />
                  <Line type="monotone" dataKey="Gulf" stroke={COLORS[2]} name="Gulf" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12}>
            <MetricsCard 
              title="Regional Performance Metrics"
              metrics={[
                { 
                  name: 'Middle East', 
                  metrics: [
                    { label: 'Audience Size', value: regionalAnalytics?.metrics?.middleEast?.audienceSize || '1.2M' },
                    { label: 'Engagement Rate', value: regionalAnalytics?.metrics?.middleEast?.engagementRate || '3.5%' },
                    { label: 'Growth Rate', value: regionalAnalytics?.metrics?.middleEast?.growthRate || '+5.2%' },
                  ]
                },
                { 
                  name: 'North Africa', 
                  metrics: [
                    { label: 'Audience Size', value: regionalAnalytics?.metrics?.northAfrica?.audienceSize || '950K' },
                    { label: 'Engagement Rate', value: regionalAnalytics?.metrics?.northAfrica?.engagementRate || '2.8%' },
                    { label: 'Growth Rate', value: regionalAnalytics?.metrics?.northAfrica?.growthRate || '+3.1%' },
                  ]
                },
                { 
                  name: 'Gulf', 
                  metrics: [
                    { label: 'Audience Size', value: regionalAnalytics?.metrics?.gulf?.audienceSize || '820K' },
                    { label: 'Engagement Rate', value: regionalAnalytics?.metrics?.gulf?.engagementRate || '4.2%' },
                    { label: 'Growth Rate', value: regionalAnalytics?.metrics?.gulf?.growthRate || '+8.7%' },
                  ]
                },
              ]}
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Cities & Regions Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Top 10 Cities by Audience Size">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={cityData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" />
                  <Tooltip />
                  <Bar dataKey="value" fill={theme.palette.primary.main} name="Audience" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Engagement Rates by Region">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={engagementByRegion}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="region" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="engagement" name="Engagement Rate (%)" fill={theme.palette.primary.main} />
                  <Bar dataKey="reach" name="Reach (%)" fill={theme.palette.secondary.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12}>
            <ChartCard title="Regional Growth Trends">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={regionalGrowth}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="MiddleEast" stackId="1" stroke={COLORS[0]} fill={COLORS[0]} name="Middle East" />
                  <Area type="monotone" dataKey="NorthAfrica" stackId="1" stroke={COLORS[1]} fill={COLORS[1]} name="North Africa" />
                  <Area type="monotone" dataKey="Gulf" stackId="1" stroke={COLORS[2]} fill={COLORS[2]} name="Gulf" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Languages Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Content Distribution by Language">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={languageData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {languageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Language Engagement Comparison">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: 'Arabic', engagement: 4.2, reach: 55 },
                    { name: 'English', engagement: 3.8, reach: 30 },
                    { name: 'French', engagement: 2.5, reach: 10 },
                    { name: 'Other', engagement: 1.9, reach: 5 },
                  ]}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" label={{ value: 'Engagement (%)', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Reach (%)', angle: 90, position: 'insideRight' }} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="engagement" name="Engagement Rate (%)" fill={theme.palette.primary.main} />
                  <Bar yAxisId="right" dataKey="reach" name="Reach (%)" fill={theme.palette.secondary.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Language Performance Insights</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Arabic Content</Typography>
                    <Typography variant="body2" paragraph>
                      Primary audience language with highest engagement rates. 
                      Video content performs 30% better than other content types.
                    </Typography>
                    <Typography variant="subtitle2">Recommendation:</Typography>
                    <Typography variant="body2">
                      Increase video content in Arabic, especially for the Gulf region where 
                      it receives the highest engagement.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">English Content</Typography>
                    <Typography variant="body2" paragraph>
                      Second most popular language, with strongest performance in UAE and Saudi.
                      Image-based content performs best.
                    </Typography>
                    <Typography variant="subtitle2">Recommendation:</Typography>
                    <Typography variant="body2">
                      Target younger demographics with English image and story content in urban 
                      centers for best engagement.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Multilingual Strategy</Typography>
                    <Typography variant="body2" paragraph>
                      Bilingual posts (Arabic/English) show 15% higher reach but slightly lower
                      engagement compared to single-language posts.
                    </Typography>
                    <Typography variant="subtitle2">Recommendation:</Typography>
                    <Typography variant="body2">
                      Use bilingual approach for awareness campaigns, but targeted language 
                      content for conversion-focused campaigns.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Performance Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard title="Content Performance by Region">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={contentPerformanceByRegion}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="MiddleEast" name="Middle East" fill={COLORS[0]} />
                  <Bar dataKey="NorthAfrica" name="North Africa" fill={COLORS[1]} />
                  <Bar dataKey="Gulf" name="Gulf" fill={COLORS[2]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Audience Growth Rate by Region">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={[
                    { month: 'Jan', MiddleEast: 1.2, NorthAfrica: 0.8, Gulf: 1.5 },
                    { month: 'Feb', MiddleEast: 1.5, NorthAfrica: 1.0, Gulf: 1.8 },
                    { month: 'Mar', MiddleEast: 1.8, NorthAfrica: 1.2, Gulf: 2.2 },
                    { month: 'Apr', MiddleEast: 2.0, NorthAfrica: 1.5, Gulf: 2.7 },
                    { month: 'May', MiddleEast: 2.5, NorthAfrica: 1.8, Gulf: 3.5 },
                    { month: 'Jun', MiddleEast: 3.0, NorthAfrica: 2.2, Gulf: 4.2 },
                  ]}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis label={{ value: 'Growth Rate (%)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="MiddleEast" name="Middle East" stroke={COLORS[0]} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="NorthAfrica" name="North Africa" stroke={COLORS[1]} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="Gulf" name="Gulf" stroke={COLORS[2]} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Conversion Rates by Region">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: 'Website Visits', MiddleEast: 3.2, NorthAfrica: 2.5, Gulf: 4.1 },
                    { name: 'Lead Generation', MiddleEast: 1.8, NorthAfrica: 1.2, Gulf: 2.3 },
                    { name: 'Sales Conversion', MiddleEast: 0.8, NorthAfrica: 0.5, Gulf: 1.2 },
                  ]}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Conversion Rate (%)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="MiddleEast" name="Middle East" fill={COLORS[0]} />
                  <Bar dataKey="NorthAfrica" name="North Africa" fill={COLORS[1]} />
                  <Bar dataKey="Gulf" name="Gulf" fill={COLORS[2]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Regional Performance Recommendations</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Gulf Region</Typography>
                    <Typography variant="body2" paragraph>
                      Highest growth and engagement rates across all content types.
                      Highest conversion rates for promotional content.
                    </Typography>
                    <Typography variant="subtitle2">Recommendation:</Typography>
                    <Typography variant="body2">
                      Increase budget allocation by 15% for Gulf-focused campaigns.
                      Prioritize video content and Instagram Stories.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Middle East (Non-Gulf)</Typography>
                    <Typography variant="body2" paragraph>
                      Steady growth with strong engagement on educational content.
                      High reach but moderate conversion rates.
                    </Typography>
                    <Typography variant="subtitle2">Recommendation:</Typography>
                    <Typography variant="body2">
                      Focus on awareness and consideration content.
                      Increase educational content in Arabic to build brand loyalty.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">North Africa</Typography>
                    <Typography variant="body2" paragraph>
                      Growing market with cost-effective reach.
                      Lower conversion rates but significant growth potential.
                    </Typography>
                    <Typography variant="subtitle2">Recommendation:</Typography>
                    <Typography variant="body2">
                      Implement region-specific campaigns focusing on value propositions.
                      Test multilingual (Arabic/French) content for better engagement.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default RegionalAnalytics;